import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';

/**
 * MenuPermissionsEditor (Hierarchical Edition)
 *
 * Two-level permission structure:
 *   1. Module level — master toggle per module (Communications, Helpdesk, FSM, VSM, Admin)
 *   2. Item level   — individual permissions inside each module
 *
 * When a module is toggled OFF, all its child permissions are removed.
 * When toggled ON, the module's items become available (defaulted to all enabled).
 *
 * Modules disabled at the org level (organization_modules table) are hidden entirely.
 *
 * Props (unchanged contract for backwards compatibility):
 *   - agentId            : agent's ID (currently informational only)
 *   - currentPermissions : array of permission_key strings
 *   - onSave(perms)      : called with the final array of permission keys
 *   - onCancel()         : called when the user cancels
 *   - agentRole          : (optional) role of the agent being edited — used to filter admin-only items
 */

// ============================================================
// Module metadata (icons + labels). Keys must match permission_catalog.module_key.
// ============================================================
const MODULE_META = {
  communications: {
    icon: '💬',
    label_en: 'Communications',
    label_ar: 'التواصل',
  },
  helpdesk: {
    icon: '🎫',
    label_en: 'Helpdesk',
    label_ar: 'الدعم الفني',
  },
  field_service: {
    icon: '🔧',
    label_en: 'Field Service',
    label_ar: 'الخدمة الميدانية',
  },
  vehicle_service: {
    icon: '🚗',
    label_en: 'Vehicle Service',
    label_ar: 'صيانة المركبات',
  },
  admin: {
    icon: '⚙️',
    label_en: 'Administration',
    label_ar: 'الإدارة',
  },
};

const MODULE_ORDER = ['communications', 'helpdesk', 'field_service', 'vehicle_service', 'admin'];

// ============================================================
// Component
// ============================================================
const MenuPermissionsEditor = ({
  agentId,
  currentPermissions = [],
  onSave,
  onCancel,
  agentRole = 'agent',
}) => {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';

  const [permissions, setPermissions] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [defaults, setDefaults] = useState({ admin: [], supervisor: [], agent: [] });
  const [orgModules, setOrgModules] = useState({});
  const [collapsedModules, setCollapsedModules] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ============================================================
  // Load catalog from backend
  // ============================================================
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch('/api/v1/agents/menu-permissions', {
          headers: { Authorization: 'Bearer ' + token },
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || 'Failed to load catalog');
        setCatalog(data.data || []);
        setDefaults(data.defaults || { admin: [], supervisor: [], agent: [] });
      } catch (err) {
        console.error('[MenuPermissionsEditor] catalog load failed:', err);
        setError(err.message || 'Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };
    fetchCatalog();
  }, []);

  // ============================================================
  // Load org modules (which modules are enabled for this org)
  // ============================================================
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        setOrgModules(u.org_modules || {});
      }
    } catch {
      setOrgModules({});
    }
  }, []);

  // ============================================================
  // Initialize permissions when prop changes
  // ============================================================
  useEffect(() => {
    setPermissions(Array.isArray(currentPermissions) ? [...currentPermissions] : []);
  }, [currentPermissions]);

  // ============================================================
  // Group catalog by module, filter by org modules, filter admin_only by role
  // ============================================================
  const groupedCatalog = useMemo(() => {
    const groups = {};
    catalog.forEach((p) => {
      // Hide admin-only permissions when editing a non-admin agent
      if (p.adminOnly && agentRole !== 'admin' && agentRole !== 'supervisor') return;
      // Hide if org doesn't have this module
      const mod = orgModules[p.module];
      if (mod && mod.enabled === false) return;
      if (!groups[p.module]) groups[p.module] = [];
      groups[p.module].push(p);
    });
    // Sort each group by sortOrder
    Object.values(groups).forEach((arr) => arr.sort((a, b) => a.sortOrder - b.sortOrder));
    return groups;
  }, [catalog, orgModules, agentRole]);

  // ============================================================
  // Filter by search term
  // ============================================================
  const filteredGrouped = useMemo(() => {
    if (!searchTerm.trim()) return groupedCatalog;
    const term = searchTerm.toLowerCase();
    const result = {};
    Object.entries(groupedCatalog).forEach(([modKey, items]) => {
      const matched = items.filter((p) => {
        const haystack = [p.label, p.labelAr, p.description, p.descriptionAr, p.key]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
      if (matched.length > 0) result[modKey] = matched;
    });
    return result;
  }, [groupedCatalog, searchTerm]);

  // ============================================================
  // Handlers
  // ============================================================
  const togglePermission = (key) => {
    setPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const isModuleEnabled = (modKey) => {
    const items = groupedCatalog[modKey] || [];
    return items.some((p) => permissions.includes(p.key));
  };

  const toggleModule = (modKey) => {
    const items = groupedCatalog[modKey] || [];
    const itemKeys = items.map((p) => p.key);
    const enabled = isModuleEnabled(modKey);

    setPermissions((prev) => {
      if (enabled) {
        // Disable: remove all items of this module
        return prev.filter((p) => !itemKeys.includes(p));
      } else {
        // Enable: add all items of this module (dedupe)
        return Array.from(new Set([...prev, ...itemKeys]));
      }
    });
  };

  const toggleCollapse = (modKey) => {
    setCollapsedModules((prev) => ({ ...prev, [modKey]: !prev[modKey] }));
  };

  const applyPreset = (presetKey) => {
    const preset = defaults[presetKey] || [];
    setPermissions([...preset]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(permissions);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // i18n helpers
  // ============================================================
  const t = (en, ar) => (isAr ? ar : en);

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div style={styles.modal(dir)}>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          {t('Loading permissions...', 'جارٍ تحميل الصلاحيات...')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.modal(dir)}>
        <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
          {t('Error: ', 'خطأ: ')}{error}
        </div>
        <div style={{ textAlign: 'center' }}>
          <button onClick={onCancel} style={styles.btnSecondary}>
            {t('Close', 'إغلاق')}
          </button>
        </div>
      </div>
    );
  }

  const visibleModuleKeys = MODULE_ORDER.filter((k) => filteredGrouped[k]?.length > 0);
  const totalEnabled = permissions.length;
  const totalAvailable = catalog.filter((p) => {
    if (p.adminOnly && agentRole !== 'admin' && agentRole !== 'supervisor') return false;
    const mod = orgModules[p.module];
    if (mod && mod.enabled === false) return false;
    return true;
  }).length;

  return (
    <div style={styles.modal(dir)} dir={dir}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>
          🔐 {t('Menu Permissions', 'صلاحيات القائمة')}
        </h3>
        <span style={styles.summary}>
          {totalEnabled} / {totalAvailable} {t('enabled', 'مفعّلة')}
        </span>
      </div>

      {/* Quick presets */}
      <div style={styles.presetsRow}>
        <span style={styles.presetsLabel}>
          {t('Quick Presets', 'قوالب سريعة')}:
        </span>
        <button onClick={() => applyPreset('admin')} style={styles.presetBtn}>
          👑 {t('Admin', 'مدير')}
        </button>
        <button onClick={() => applyPreset('supervisor')} style={styles.presetBtn}>
          👔 {t('Supervisor', 'مشرف')}
        </button>
        <button onClick={() => applyPreset('agent')} style={styles.presetBtn}>
          👤 {t('Agent', 'موظف')}
        </button>
        <button onClick={() => setPermissions([])} style={styles.presetBtnDanger}>
          ✕ {t('Clear', 'مسح')}
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={t('Search permissions...', 'ابحث في الصلاحيات...')}
        style={styles.searchInput(dir)}
      />

      {/* Modules */}
      <div style={styles.modulesContainer}>
        {visibleModuleKeys.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>
            {t('No matching permissions.', 'لا توجد صلاحيات مطابقة.')}
          </div>
        )}
        {visibleModuleKeys.map((modKey) => {
          const meta = MODULE_META[modKey] || { icon: '📋', label_en: modKey, label_ar: modKey };
          const items = filteredGrouped[modKey];
          const enabled = isModuleEnabled(modKey);
          const collapsed = collapsedModules[modKey];
          const enabledCount = items.filter((p) => permissions.includes(p.key)).length;

          return (
            <div key={modKey} style={styles.moduleBox(enabled)}>
              {/* Module header */}
              <div style={styles.moduleHeader} onClick={() => toggleCollapse(modKey)}>
                <span style={styles.moduleIcon}>{meta.icon}</span>
                <span style={styles.moduleLabel}>
                  {isAr ? meta.label_ar : meta.label_en}
                </span>
                <span style={styles.moduleCount}>
                  {enabledCount} / {items.length}
                </span>

                {/* Collapse arrow */}
                <span style={styles.collapseArrow}>
                  {collapsed ? (isAr ? '◀' : '▶') : '▼'}
                </span>

                {/* Master toggle (stop propagation so it doesn't collapse) */}
                <label
                  style={styles.masterToggle}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={() => toggleModule(modKey)}
                    style={styles.masterCheckbox}
                  />
                  <span style={styles.masterText(enabled)}>
                    {enabled ? t('ON', 'مفعّل') : t('OFF', 'معطّل')}
                  </span>
                </label>
              </div>

              {/* Module items */}
              {!collapsed && (
                <div style={styles.itemsGrid}>
                  {items.map((p) => {
                    const isChecked = permissions.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        style={styles.itemCard(isChecked)}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePermission(p.key)}
                          style={styles.itemCheckbox}
                        />
                        <span style={styles.itemIcon}>{p.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.itemLabel(isChecked)}>
                            {isAr ? p.labelAr : p.label}
                            {p.adminOnly && (
                              <span style={styles.adminBadge}>
                                {t('Admin', 'مدير')}
                              </span>
                            )}
                          </div>
                          {(isAr ? p.descriptionAr : p.description) && (
                            <div style={styles.itemDesc}>
                              {isAr ? p.descriptionAr : p.description}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <button onClick={onCancel} disabled={saving} style={styles.btnSecondary}>
          {t('Cancel', 'إلغاء')}
        </button>
        <button onClick={handleSave} disabled={saving} style={styles.btnPrimary(saving)}>
          {saving ? t('Saving...', 'جارٍ الحفظ...') : t('Save Permissions', 'حفظ الصلاحيات')}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Styles (kept inline for backwards compatibility with existing modal pattern)
// ============================================================
const styles = {
  modal: (dir) => ({
    backgroundColor: 'rgba(26, 32, 56, 0.98)',
    borderRadius: '12px',
    padding: '20px',
    maxWidth: '720px',
    width: '100%',
    maxHeight: '85vh',
    overflow: 'auto',
    direction: dir,
    color: '#f9fafb',
  }),
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
  },
  summary: {
    fontSize: '13px',
    color: '#a5b4fc',
    fontWeight: 500,
    backgroundColor: 'rgba(102, 126, 234, 0.15)',
    padding: '4px 12px',
    borderRadius: '999px',
  },
  presetsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '14px',
    alignItems: 'center',
  },
  presetsLabel: {
    fontSize: '12px',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  presetBtn: {
    padding: '6px 12px',
    backgroundColor: 'rgba(102, 126, 234, 0.12)',
    color: '#a5b4fc',
    border: '1px solid rgba(102, 126, 234, 0.3)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  presetBtnDanger: {
    padding: '6px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#f87171',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  searchInput: (dir) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 14px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#f9fafb',
    fontSize: '14px',
    marginBottom: '14px',
    direction: dir,
    textAlign: dir === 'rtl' ? 'right' : 'left',
  }),
  modulesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  moduleBox: (enabled) => ({
    border: enabled
      ? '1px solid rgba(34, 197, 94, 0.35)'
      : '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    backgroundColor: enabled
      ? 'rgba(34, 197, 94, 0.05)'
      : 'rgba(255, 255, 255, 0.02)',
    overflow: 'hidden',
  }),
  moduleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    cursor: 'pointer',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  moduleIcon: {
    fontSize: '20px',
  },
  moduleLabel: {
    flex: 1,
    fontSize: '15px',
    fontWeight: 600,
    color: '#f9fafb',
  },
  moduleCount: {
    fontSize: '12px',
    color: '#9ca3af',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: '2px 10px',
    borderRadius: '999px',
  },
  collapseArrow: {
    fontSize: '10px',
    color: '#6b7280',
    width: '14px',
    textAlign: 'center',
  },
  masterToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    padding: '4px 10px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '6px',
  },
  masterCheckbox: {
    width: '16px',
    height: '16px',
    accentColor: '#22c55e',
    cursor: 'pointer',
  },
  masterText: (enabled) => ({
    fontSize: '12px',
    fontWeight: 600,
    color: enabled ? '#22c55e' : '#9ca3af',
  }),
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '8px',
    padding: '12px',
  },
  itemCard: (checked) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px',
    backgroundColor: checked ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255, 255, 255, 0.02)',
    border: checked
      ? '1px solid rgba(34, 197, 94, 0.3)'
      : '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  }),
  itemCheckbox: {
    width: '16px',
    height: '16px',
    accentColor: '#22c55e',
    cursor: 'pointer',
    marginTop: '2px',
    flexShrink: 0,
  },
  itemIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  itemLabel: (checked) => ({
    fontSize: '13px',
    fontWeight: 500,
    color: checked ? '#f9fafb' : '#d1d5db',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  }),
  itemDesc: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
    lineHeight: 1.3,
  },
  adminBadge: {
    fontSize: '9px',
    color: '#fbbf24',
    backgroundColor: 'rgba(251, 191, 36, 0.15)',
    padding: '1px 6px',
    borderRadius: '4px',
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '14px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  btnSecondary: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#9ca3af',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  btnPrimary: (saving) => ({
    padding: '10px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: saving ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    opacity: saving ? 0.7 : 1,
  }),
};

export default MenuPermissionsEditor;
